import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ActionButton from 'jbc-front/components/ActionButton';
import { PlusCircle } from 'jbc-front/components/icons';

import { formatInt, useTaxAllocationTotals } from '../utils/declarationUtils';
import styles from './TaxAllocationAppendixTab.scss';
import DeclarationInfoSection from './DeclarationInfoSection';
import AllocationRow from './AllocationRow';

const TaxAllocationAppendixTab = ({
  declaration,
  branchAllocations,
  isEditing,
  allocationsDraft,
  onAllocationDraftChange,
  onAllocationBatchUpdate,
  onAddAllocation,
  onDeleteAllocation,
  dependentBranches,
  provinces,
  subdistricts,
  regionalTaxOffices,
  taxOffices,
  errorFields
}) => {
  const { t, i18n } = useTranslation();

  const getLocalizedLabel = useCallback(item => (i18n.language === 'vi' ? item.label : item.labelEn), [i18n.language]);

  const provinceOptions = useMemo(
    () => {
      if (!provinces) return [];
      return provinces.map(p => ({ value: String(p.value), label: getLocalizedLabel(p) }));
    },
    [provinces, getLocalizedLabel]
  );

  const getSubdistrictOptions = useCallback(
    provinceId => {
      if (!subdistricts || !provinceId) return [];
      const filtered = subdistricts[provinceId] || [];
      return filtered.map(s => ({ value: String(s.value), label: getLocalizedLabel(s) }));
    },
    [subdistricts, getLocalizedLabel]
  );

  const getRegionalTaxOfficeOptions = useCallback(
    provinceId => {
      if (!regionalTaxOffices || !provinceId) return [];
      const provincialOffices = regionalTaxOffices[provinceId] || [];
      const specialOffices = regionalTaxOffices[''] || [];
      const allOffices = [...provincialOffices, ...specialOffices];
      const unique = allOffices.filter((o, index, self) => index === self.findIndex(item => item.taxOfficeCode === o.taxOfficeCode));
      return unique.map(r => ({ value: r.taxOfficeCode, label: r.label }));
    },
    [regionalTaxOffices]
  );

  const getTaxOfficeOptions = useCallback(
    regionalCode => {
      if (!taxOffices || !regionalCode) return [];
      const allOffices = Object.values(taxOffices).flat();
      const filtered = allOffices.filter(o => o.parentTaxOfficeCode === regionalCode);
      const unique = filtered.filter((o, index, self) => index === self.findIndex(item => item.taxOfficeCode === o.taxOfficeCode));
      return unique.map(o => ({ value: o.taxOfficeCode, label: o.label }));
    },
    [taxOffices]
  );

  const handleProvinceChange = useCallback(
    id => value => {
      const provinceId = value || '';
      const province = provinces && provinces.find(p => String(p.value) === String(provinceId));
      onAllocationBatchUpdate(id, {
        provinceId,
        provinceName: (province && province.label) || '',
        districtId: '',
        districtName: '',
        regionalTaxOfficeCode: '',
        taxOfficeCode: '',
        taxOfficeName: '',
        parentTaxOfficeName: ''
      });
    },
    [provinces, onAllocationBatchUpdate]
  );

  const handleSubdistrictChange = useCallback(
    (id, provinceId) => value => {
      const subdistrictId = value || '';
      const subdistrictList = (subdistricts && subdistricts[provinceId]) || [];
      const subdistrict = subdistrictList.find(s => String(s.value) === String(subdistrictId));
      onAllocationBatchUpdate(id, {
        districtId: subdistrictId,
        districtName: (subdistrict && subdistrict.label) || ''
      });
    },
    [subdistricts, onAllocationBatchUpdate]
  );

  const handleRegionalTaxOfficeChange = useCallback(
    id => value => {
      const regionalCode = value || '';
      onAllocationBatchUpdate(id, {
        regionalTaxOfficeCode: regionalCode,
        taxOfficeCode: '',
        taxOfficeName: ''
      });
    },
    [onAllocationBatchUpdate]
  );

  const handleTaxOfficeChange = useCallback(
    id => value => {
      const taxOfficeCode = value || '';
      const allOffices = Object.values(taxOffices || {}).flat();
      const office = allOffices.find(o => o.taxOfficeCode === taxOfficeCode);
      onAllocationBatchUpdate(id, {
        taxOfficeCode,
        taxOfficeName: (office && office.label) || ''
      });
    },
    [taxOffices, onAllocationBatchUpdate]
  );

  const visibleAllocations = useMemo(
    () => {
      if (!isEditing || !allocationsDraft) return branchAllocations;
      return allocationsDraft.filter(a => !a.destroy);
    },
    [isEditing, allocationsDraft, branchAllocations]
  );

  const availableBranches = useMemo(
    () => {
      if (!dependentBranches) return [];
      const existingBranchIds = visibleAllocations.map(a => a.clientBranchId);
      return dependentBranches.filter(b => !existingBranchIds.includes(b.id));
    },
    [dependentBranches, visibleAllocations]
  );

  const totals = useTaxAllocationTotals(visibleAllocations);

  const handleAddEmptyRow = () => {
    if (!onAddAllocation) return;
    onAddAllocation(null);
  };

  const handleDelete = allocId => {
    if (!onDeleteAllocation) return;
    onDeleteAllocation(allocId);
  };

  const renderAmount = value => formatInt(value);

  return (
    <div className="tax-allocation-appendix-tab">
      {/* Form Header */}
      <DeclarationInfoSection declaration={declaration} title={t('features.ReportIncomeTax.show.appendix.title')} maxCode="05" />

      {/* Section I: Tax Allocation Table */}
      <div className="declaration-form-section-wrapper">
        <div className="declaration-form-section">
          <div className="declaration-form-section__header">{t('features.ReportIncomeTax.show.appendix.section_title')}</div>
          <div className={`declaration-form-section__body ${styles.sectionBody}`}>
            <div className="appendix-header">
              <div />
              <div className="appendix-header__currency">
                {t('features.ReportIncomeTax.show.appendix.currency')}: <strong>VND</strong>
              </div>
            </div>

            <div className="appendix-add-btn">
              <ActionButton
                onClick={handleAddEmptyRow}
                icon={<PlusCircle size={16} />}
                className="appendix-add-btn__button"
                primary
                disabled={!isEditing || availableBranches.length === 0 || visibleAllocations.some(a => a.isEmpty)}
              >
                {t('features.ReportIncomeTax.show.appendix.add_unit')}
              </ActionButton>
            </div>

            <div className="appendix-table-wrapper l-overflow-scroll">
              <table className="declaration-data-table appendix-table">
                <thead>
                  <tr>
                    <th rowSpan="2" className="col-no">
                      <span className="th-code">[06]</span> {t('features.ReportIncomeTax.show.appendix.table.stt')}
                    </th>
                    <th colSpan="2">[07] {t('features.ReportIncomeTax.show.appendix.table.expense_type')}</th>
                    <th rowSpan="2">
                      <span className="th-code">[08]</span> {t('features.ReportIncomeTax.show.appendix.table.tax_code')}
                    </th>
                    <th colSpan="2">[08b] {t('features.ReportIncomeTax.show.appendix.table.business_location')}</th>
                    <th colSpan="2">[09] {t('features.ReportIncomeTax.show.appendix.table.tax_authority')}</th>
                    <th rowSpan="2" className="col-amount">
                      <span className="th-code">[10]</span> {t('features.ReportIncomeTax.show.appendix.table.ct10')}
                    </th>
                    <th rowSpan="2" className="col-amount">
                      <span className="th-code">[11]</span> {t('features.ReportIncomeTax.show.appendix.table.ct11')}
                    </th>
                    <th rowSpan="2" className="col-amount">
                      <span className="th-code">[12]</span> {t('features.ReportIncomeTax.show.appendix.table.ct12')}
                    </th>
                    <th rowSpan="2" className="col-amount">
                      <span className="th-code">[13]</span> {t('features.ReportIncomeTax.show.appendix.table.ct13')}
                    </th>
                    <th rowSpan="2" className="col-amount">
                      <span className="th-code">[14]</span> {t('features.ReportIncomeTax.show.appendix.table.ct14')}
                    </th>
                    {isEditing && (
                      <th rowSpan="2" className="col-action">
                        {t('features.ReportIncomeTax.show.appendix.table.action')}
                      </th>
                    )}
                  </tr>
                  <tr>
                    <th>{t('features.ReportIncomeTax.show.appendix.table.expense_type_col')}</th>
                    <th>{t('features.ReportIncomeTax.show.appendix.table.head_office_unit')}</th>
                    <th>{t('features.ReportIncomeTax.show.appendix.table.province')}</th>
                    <th>{t('features.ReportIncomeTax.show.appendix.table.ward_district')}</th>
                    <th>{t('features.ReportIncomeTax.show.appendix.table.department_level')}</th>
                    <th>{t('features.ReportIncomeTax.show.appendix.table.managing_authority')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAllocations.map((allocation, index) => (
                    <AllocationRow
                      key={allocation.id || allocation.tempId}
                      allocation={allocation}
                      index={index}
                      isEditing={isEditing}
                      availableBranches={availableBranches}
                      provinceOptions={provinceOptions}
                      getSubdistrictOptions={getSubdistrictOptions}
                      getRegionalTaxOfficeOptions={getRegionalTaxOfficeOptions}
                      getTaxOfficeOptions={getTaxOfficeOptions}
                      handleProvinceChange={handleProvinceChange}
                      handleSubdistrictChange={handleSubdistrictChange}
                      handleRegionalTaxOfficeChange={handleRegionalTaxOfficeChange}
                      handleTaxOfficeChange={handleTaxOfficeChange}
                      onDraftChange={onAllocationDraftChange}
                      onBatchUpdate={onAllocationBatchUpdate}
                      onDelete={handleDelete}
                      errorFields={errorFields}
                    />
                  ))}

                  {/* Total Row */}
                  {visibleAllocations.length > 0 && (
                    <tr className="total-row">
                      <td colSpan="8" className={styles.totalLabel}>
                        {t('features.ReportIncomeTax.show.appendix.table.total')}
                      </td>
                      <td className="col-amount">{renderAmount(totals.ct10)}</td>
                      <td className="col-amount">{renderAmount(totals.ct11)}</td>
                      <td className="col-amount">{renderAmount(totals.ct12)}</td>
                      <td className="col-amount">{renderAmount(totals.ct13)}</td>
                      <td className="col-amount">{renderAmount(totals.ct14)}</td>
                      {isEditing && <td />}
                    </tr>
                  )}

                  {/* Empty state */}
                  {visibleAllocations.length === 0 && (
                    <tr>
                      <td colSpan={isEditing ? 14 : 13} className={styles.noData}>
                        {t('features.ReportIncomeTax.show.appendix.no_data')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

TaxAllocationAppendixTab.propTypes = {
  declaration: PropTypes.shape({
    periodType: PropTypes.string,
    periodYear: PropTypes.number,
    periodMonth: PropTypes.number,
    periodQuarter: PropTypes.number,
    supplementaryNo: PropTypes.number,
    companyName: PropTypes.string,
    companyTaxCode: PropTypes.string,
    ct15HasMultiProvinceAlloc: PropTypes.bool
  }).isRequired,
  branchAllocations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      clientBranchId: PropTypes.number,
      branchType: PropTypes.string,
      branchName: PropTypes.string,
      provinceName: PropTypes.string,
      districtName: PropTypes.string,
      taxOfficeName: PropTypes.string,
      parentTaxOfficeName: PropTypes.string,
      taxCodeLocation: PropTypes.string,
      ct10TaxableIncome: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      ct11TaxableIncomeSubjectToWh: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      ct12EmployeeCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      ct13EmployeesWithTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      ct14TaxWithheld: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  ),
  isEditing: PropTypes.bool,
  allocationsDraft: PropTypes.array,
  onAllocationDraftChange: PropTypes.func,
  onAllocationBatchUpdate: PropTypes.func,
  onAddAllocation: PropTypes.func,
  onDeleteAllocation: PropTypes.func,
  dependentBranches: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      provinceId: PropTypes.number,
      provinceName: PropTypes.string
    })
  ),
  provinces: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number, label: PropTypes.string, labelEn: PropTypes.string })),
  subdistricts: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number, label: PropTypes.string, labelEn: PropTypes.string }))),
  regionalTaxOffices: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({ taxOfficeCode: PropTypes.string, label: PropTypes.string }))),
  taxOffices: PropTypes.objectOf(
    PropTypes.arrayOf(PropTypes.shape({ taxOfficeCode: PropTypes.string, parentTaxOfficeCode: PropTypes.string, label: PropTypes.string }))
  ),
  errorFields: PropTypes.instanceOf(Set)
};

TaxAllocationAppendixTab.defaultProps = {
  branchAllocations: [],
  isEditing: false,
  allocationsDraft: null,
  onAllocationDraftChange: null,
  onAllocationBatchUpdate: null,
  onAddAllocation: null,
  onDeleteAllocation: null,
  dependentBranches: [],
  provinces: [],
  subdistricts: {},
  regionalTaxOffices: {},
  taxOffices: {},
  errorFields: null
};

export default TaxAllocationAppendixTab;
